import { useEffect, useState } from "react";
import { api, fileUrl, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { ImageUpload } from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { CATEGORY_COLORS, formatDate } from "../lib/categories";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Search, User, Users, Loader2, FileText, FileSpreadsheet, BadgeCheck, CreditCard } from "lucide-react";

const empty = { first_name: "", last_name: "", dob: "", photo_path: "" };

export default function ClubDashboard() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [payMember, setPayMember] = useState(null);
  const [payInfo, setPayInfo] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payProcessing, setPayProcessing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);

  const load = (season) => {
    setLoading(true);
    const q = season ? `?season=${encodeURIComponent(season)}` : "";
    api.get(`/members${q}`).then((res) => setMembers(res.data)).finally(() => setLoading(false));
  };
  const loadMeta = async () => {
    try {
      const [s, ss] = await Promise.all([api.get("/settings"), api.get("/members/seasons")]);
      setSettings(s.data);
      setSeasons(ss.data);
      setSelectedSeason(s.data.active_season);
      load(s.data.active_season);
    } catch {
      load(selectedSeason);
    }
  };
  useEffect(() => { loadMeta(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ first_name: m.first_name, last_name: m.last_name, dob: m.dob, photo_path: m.photo_path || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/members/${editing.id}`, form);
        toast.success("Član ažuriran");
      } else {
        await api.post("/members", form);
        toast.success("Član dodat");
      }
      setOpen(false);
      load(selectedSeason);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/members/${id}`);
      toast.success("Član obrisan");
      load(selectedSeason);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/members/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clanovi.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Izvoz nije uspio");
    } finally {
      setExporting("");
    }
  };

  const filtered = members.filter((m) =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(query.toLowerCase())
  );
  const licensedCount = members.filter((m) => m.licensed).length;

  const startPayment = async (m) => {
    setPayMember(m); setPayInfo(null); setPayLoading(true);
    try {
      const res = await api.post("/payments/create", { member_id: m.id });
      setPayInfo(res.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setPayMember(null);
    } finally {
      setPayLoading(false);
    }
  };

  const confirmPayment = async (success) => {
    if (!payInfo) return;
    setPayProcessing(true);
    try {
      await api.post(`/payments/${payInfo.payment_id}/mock-confirm`, { success });
      if (success) toast.success("Uplata uspješna — član licenciran");
      else toast.error("Uplata nije uspjela (test)");
      setPayMember(null); setPayInfo(null);
      load(selectedSeason);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setPayProcessing(false);
    }
  };

  const activeSeason = settings?.active_season;
  const isArchive = !!(selectedSeason && activeSeason && selectedSeason !== activeSeason);
  const showLicensing = settings ? settings.license_mode !== "off" : true;
  const onlineMode = settings?.license_mode === "online";

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Klub panel</div>
            <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="club-title">{user?.name}</h1>
            <p className="text-muted-foreground mt-1">{user?.city}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-none border-2 h-11" onClick={() => exportFile("pdf")} disabled={exporting === "pdf"} data-testid="export-pdf-btn">
              {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />} PDF
            </Button>
            <Button variant="outline" className="rounded-none border-2 h-11" onClick={() => exportFile("xlsx")} disabled={exporting === "xlsx"} data-testid="export-excel-btn">
              {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Excel
            </Button>
            <Button className="rounded-none bg-primary hover:bg-primary/90 h-11" onClick={openNew} disabled={isArchive} title={isArchive ? "Dodavanje je moguće samo u aktivnoj sezoni" : ""} data-testid="add-member-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Dodaj člana
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className={`flex flex-wrap items-center gap-5 mb-6 text-sm ${showLicensing ? "" : "hidden"}`}>
          <div className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 border border-sky-400/50 bg-sky-500/30" /> Licencirani ({licensedCount})
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 border border-white/20 bg-white/10" /> Bez licence ({members.length - licensedCount})
          </div>
          <span className="text-muted-foreground text-xs">Licencu odobrava master UPUS CG.</span>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="rounded-none border-2 pl-9" placeholder="Pretraži članove..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="member-search" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Sezona</span>
            <select
              value={selectedSeason || ""}
              onChange={(e) => { setSelectedSeason(e.target.value); load(e.target.value); }}
              className="rounded-none border-2 border-border bg-card px-3 h-10 text-sm font-semibold"
              data-testid="season-select"
            >
              {seasons.map((s) => (<option key={s} value={s}>{s}{activeSeason === s ? " (aktivna)" : ""}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4" /> <span data-testid="member-count">{members.length}</span> članova
          </div>
        </div>

        {isArchive && (
          <div className="mb-6 text-sm bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2" data-testid="archive-notice">
            Pregledate arhivu sezone <strong>{selectedSeason}</strong>. Dodavanje novih članova je moguće samo u aktivnoj sezoni ({activeSeason}).
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Učitavanje...</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground border-2 border-dashed border-border" data-testid="no-members">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-40" />
            {members.length === 0 ? "Još nema članova. Dodajte prvog člana." : "Nema rezultata pretrage."}
          </div>
        ) : (
          <div>
            <div className="hidden md:grid grid-cols-[50px_88px_1fr_130px_140px_110px_90px] gap-4 px-4 py-3 text-xs uppercase tracking-[0.15em] text-white bg-[hsl(var(--secondary))]">
              <div>Rb.</div><div>Slika</div><div>Ime i prezime</div><div>Datum rođenja</div><div>Kategorija</div><div>{showLicensing ? "Licenca" : ""}</div><div className="text-right">Akcije</div>
            </div>
            <div className="space-y-2 mt-2">
              {filtered.map((m) => (
                <div
                  key={m.id}
                  className={`grid grid-cols-[40px_1fr_auto] md:grid-cols-[50px_88px_1fr_130px_140px_110px_90px] gap-3 md:gap-4 px-4 py-3 items-center border-2 border-primary/70 transition-colors ${!showLicensing ? "bg-white/5 hover:bg-white/10" : m.licensed ? "bg-sky-500/15 hover:bg-sky-500/25" : "bg-white/5 hover:bg-white/10"}`}
                  data-testid={`member-row-${m.id}`}
                >
                  <div><span className="inline-flex items-center justify-center min-w-[30px] px-2 py-1 bg-[hsl(var(--secondary))] text-white font-display font-bold text-sm">{String(m.serial_no).padStart(2, "0")}</span></div>
                  <div className="w-16 h-16 border border-border bg-muted overflow-hidden flex items-center justify-center md:col-auto">
                    {m.photo_path ? <img src={fileUrl(m.photo_path)} alt="" className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{m.last_name} {m.first_name}</div>
                    <div className="text-xs text-muted-foreground md:hidden">{formatDate(m.dob)} · {m.age_category}{showLicensing ? (m.licensed ? " · Licenca ✓" : " · Bez licence") : ""}</div>
                  </div>
                  <div className="hidden md:block text-sm text-muted-foreground">{formatDate(m.dob)} <span className="text-xs">({m.age})</span></div>
                  <div className="hidden md:block">
                    <span className={`inline-block text-xs font-semibold px-2 py-1 border ${CATEGORY_COLORS[m.age_category] || CATEGORY_COLORS["Van kategorije"]}`}>{m.age_category}</span>
                  </div>
                  <div className="hidden md:block">
                    {!showLicensing ? null : m.licensed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700"><BadgeCheck className="w-4 h-4" /> Licenciran</span>
                    ) : onlineMode && !isArchive ? (
                      <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90 h-7 text-xs px-2" onClick={() => startPayment(m)} data-testid={`pay-license-${m.id}`}>
                        <CreditCard className="w-3.5 h-3.5 mr-1" /> Plati licencu
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-primary" data-testid={`delete-member-${m.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none border-2">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Obrisati člana?</AlertDialogTitle>
                          <AlertDialogDescription>{m.first_name} {m.last_name} biće trajno obrisan/a.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none">Otkaži</AlertDialogCancel>
                          <AlertDialogAction className="rounded-none bg-primary hover:bg-primary/90" onClick={() => del(m.id)}>Obriši</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">
              {editing ? "Izmijeni člana" : "Novi član"}
            </DialogTitle>
            <DialogDescription>Unesite podatke člana. Uzrastna kategorija se računa automatski.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Fotografija</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} label="Sliku" shape="circle" testId="member-photo-upload" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fn">Ime *</Label>
                <Input id="fn" className="rounded-none border-2 mt-1.5" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="member-firstname" />
              </div>
              <div>
                <Label htmlFor="ln">Prezime *</Label>
                <Input id="ln" className="rounded-none border-2 mt-1.5" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="member-lastname" />
              </div>
            </div>
            <div>
              <Label htmlFor="dob">Datum rođenja *</Label>
              <Input id="dob" type="date" className="rounded-none border-2 mt-1.5" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} required data-testid="member-dob" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="member-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Sačuvaj izmjene" : "Dodaj člana"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!payMember} onOpenChange={(o) => { if (!o) { setPayMember(null); setPayInfo(null); } }}>
        <DialogContent className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Plaćanje licence</DialogTitle>
            <DialogDescription>Monri WSPay — sigurno online plaćanje karticom.</DialogDescription>
          </DialogHeader>
          {payLoading || !payInfo ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-border p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Član</span><span className="font-semibold">{payInfo.member_name}</span></div>
                <div className="flex justify-between items-center"><span className="text-muted-foreground text-sm">Godišnja licenca</span><span className="font-display font-black text-2xl">{Number(payInfo.amount).toFixed(2)} {payInfo.currency}</span></div>
              </div>
              {payInfo.mock && (
                <div className="text-xs bg-amber-100 border border-amber-300 text-amber-800 px-3 py-2" data-testid="pay-mock-notice">
                  TEST režim — Monri naplata još nije povezana (nema ključeva). Uplata se simulira; član će biti licenciran.
                </div>
              )}
              <div className="flex gap-2">
                <Button className="flex-1 rounded-none bg-primary hover:bg-primary/90 h-11" disabled={payProcessing} onClick={() => confirmPayment(true)} data-testid="pay-confirm">
                  {payProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Plati {Number(payInfo.amount).toFixed(2)} {payInfo.currency}</>}
                </Button>
                <Button variant="outline" className="rounded-none border-2 h-11" disabled={payProcessing} onClick={() => confirmPayment(false)} data-testid="pay-fail">
                  Neuspjeh
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
